/**
 * THE THREE STARTERS — a task an owner can read before she owns it.
 *
 * PURE DATA. No Vue, no store, no I/O, no translator: every word a CUSTOMER would read is
 * here in both languages, because these are not interface strings. A question on a WhatsApp
 * card is sent exactly as it is written, in the language the customer is writing in, and it
 * belongs to the task rather than to the dashboard the owner happens to be reading.
 *
 * ── WHY A STARTER, AND WHY THESE THREE ────────────────────────────────────────────────
 * A shop owner cannot judge a taxonomy. He can judge a conversation. So the first screen
 * does not ask him to pick a step type — it shows him three finished tasks as the chat his
 * own customer would have, and he picks the one that is nearest his job and edits it.
 *
 *   • BOOK SOMETHING   — a time and a place, then a person confirms it. The single most
 *                        asked-for job, and the one the whole design is worked through on.
 *   • TAKE AN ORDER    — what, how it reaches them, and who to call. The only one with a
 *                        branch in it, because "where do we deliver" is a question you must
 *                        not ask somebody who is coming to collect.
 *   • TAKE AN ENQUIRY  — what they want, their name, their number. The job every business
 *                        has whatever else it does, and the one that is pure lead capture.
 *
 * ── THE BAR EVERY ONE OF THEM MEETS, AND WHY IT IS A SPEC AND NOT A HABIT ─────────────
 *  1. `localProblems(definition)` is `[]`. A starter that cannot be saved is worse than no
 *     starter: it teaches, on the very first screen, that the product refuses its own work.
 *  2. Every `ask` and every `choose` carries a NON-EMPTY `questionEn` AND `questionAr`.
 *     The builder has never exposed either field, which is why `lang=ar` returns `flow: null`
 *     with every step marked `no_question_in_language` — an Arabic customer gets no card at
 *     all. Shipping the Arabic written closes that structurally rather than as a chore, and
 *     an owner who picks a starter opens the Arabic screen already finished.
 *  3. No `choose_live` carries either field, and none of these has a `choose_live` at all.
 *     `ChooseLiveStep` does not extend `StepQuestion` (`types.ts:131`) and `schema.ts:38`
 *     spreads `formQuestion` into `ask` and `choose` and deliberately not into it, under
 *     `.strict()` — so a question there is a key the server REFUSES, on a box the owner
 *     filled for nothing. A starter is also the wrong place for one for a plainer reason:
 *     it would name a system the account has not connected.
 *  4. Every option label is inside 20 characters IN BOTH LANGUAGES — `MAX_OPTION_LABEL`,
 *     which is WhatsApp's reply-button title cap, the tighter of its two shapes.
 *
 * `specs/task-starters.spec.js` asserts all four on every starter, and asserts the third
 * against a counterexample so it cannot pass by there being nothing to check.
 *
 * ── THE ARABIC ────────────────────────────────────────────────────────────────────────
 * Written as a Kuwaiti business writes to a customer on WhatsApp — plain, short, and in the
 * register a shop actually uses ("شنو تحب تطلب؟", not "ما الذي تودّ طلبه؟"). A card is sent
 * verbatim, so stiff Arabic on it is not a translation smell, it is what the customer reads.
 *
 * ── AND WHAT THE SCRIPT IS NOT ────────────────────────────────────────────────────────
 * `script` is the conversation's SHAPE, not a promise about wording. Inside a chat the
 * assistant phrases every step itself, from `instruction`, in the customer's own language;
 * only a form card sends `questionEn`/`questionAr` untouched. The preview says so in one
 * line rather than letting the owner infer a guarantee the engine never made.
 */

// ── one starter's shape ───────────────────────────────────────────────────────────────

const say = (textEn, textAr, extra = {}) => ({
  speaker: 'assistant',
  textEn,
  textAr,
  ...extra,
});

const typed = (textEn, textAr) => ({ speaker: 'customer', textEn, textAr });

const option = (id, labelEn, labelAr, extra = {}) => ({
  id,
  labelEn,
  labelAr,
  ...extra,
});

// ── 1 · book something ────────────────────────────────────────────────────────────────

const BOOK = {
  id: 'book_appointment',
  /** A lucide name; the card maps it to `i-lucide-<icon>`. */
  icon: 'calendar-days',
  titleEn: 'Book something',
  titleAr: 'حجز موعد',
  blurbEn:
    'Takes the branch, the day and the time, then hands the chat to you.',
  blurbAr: 'يأخذ الفرع واليوم والوقت، وبعدها ينقل المحادثة لك.',
  taskTitleEn: 'Book an appointment',
  taskTitleAr: 'حجز موعد',
  triggerEn:
    'The customer wants to book, reserve or reschedule an appointment.',
  triggerAr: 'العميل يبي يحجز موعد أو يغيّر موعده.',
  definition: {
    firstStepId: 'branch',
    steps: [
      {
        id: 'branch',
        name: 'Which branch',
        type: 'choose',
        instruction:
          'Ask which branch the customer would like. Do not list the branches yourself. They are shown as buttons.',
        questionEn: 'Which branch would you like?',
        questionAr: 'أي فرع تحب؟',
        options: [
          { id: 'salmiya', labelEn: 'Salmiya', labelAr: 'السالمية' },
          { id: 'shuwaikh', labelEn: 'Shuwaikh', labelAr: 'الشويخ' },
        ],
        saveTo: { chat: 'branch' },
        next: 'day',
      },
      {
        id: 'day',
        name: 'Which day',
        type: 'ask',
        kind: 'date',
        instruction:
          'Ask which day they would like to come in. Do not offer times yet, and do not say whether the day is free.',
        questionEn: 'Which day would you like to come in?',
        questionAr: 'أي يوم تحب تجي؟',
        saveTo: { chat: 'day' },
        next: 'visit_time',
      },
      {
        id: 'visit_time',
        name: 'What time',
        type: 'ask',
        kind: 'time',
        instruction:
          'Ask roughly what time of day suits them. Accept an approximate answer.',
        questionEn: 'What time suits you?',
        questionAr: 'أي وقت يناسبك؟',
        saveTo: { chat: 'visit_time' },
        next: 'who',
      },
      {
        id: 'who',
        name: 'Their name',
        type: 'ask',
        kind: 'text',
        instruction: 'Ask for the name the booking should be under.',
        questionEn: 'What name should we put it under?',
        questionAr: 'باسم منو نسجل الحجز؟',
        saveTo: { chat: 'caller_name', contact: 'name' },
        next: 'read_back',
      },
      {
        id: 'read_back',
        name: 'Read it back',
        type: 'tell',
        instruction:
          'Read the branch, the day and the time back to the customer, and say a colleague will confirm it shortly. Do not say the booking is confirmed, and do not promise that the time is held.',
        next: 'confirm_it',
      },
      {
        id: 'confirm_it',
        name: 'A person confirms it',
        type: 'handoff',
        note: 'A booking request is ready. The branch, the day, the time and the name are in the answers above. Confirm the time with the customer.',
      },
    ],
  },
  script: [
    typed('I want to book', 'أبي أحجز'),
    say('Which branch would you like?', 'أي فرع تحب؟', {
      options: [
        option('salmiya', 'Salmiya', 'السالمية'),
        option('shuwaikh', 'Shuwaikh', 'الشويخ'),
      ],
      noteEn: 'These will be your own branches.',
      noteAr: 'هذي بتكون فروعك أنت.',
    }),
    typed('Salmiya', 'السالمية'),
    say('Which day would you like to come in?', 'أي يوم تحب تجي؟'),
    typed('Thursday', 'الخميس'),
    say('What time suits you?', 'أي وقت يناسبك؟'),
    typed('Around 5', 'حوالي الخمس'),
    say('What name should we put it under?', 'باسم منو نسجل الحجز؟'),
    typed('Nasser', 'ناصر'),
    say(
      'Salmiya, Thursday at around 5, under Nasser. One of the team will confirm it shortly.',
      'السالمية، الخميس حوالي الخمس، باسم ناصر. أحد الزملاء بيأكد لك بعد شوي.',
      {
        noteEn: 'Then the chat comes to you, with all four answers on it.',
        noteAr: 'وبعدها تنتقل المحادثة لك، ومعها الإجابات الأربع.',
      }
    ),
  ],
};

// ── 2 · take an order ─────────────────────────────────────────────────────────────────

const ORDER = {
  id: 'take_an_order',
  icon: 'shopping-bag',
  titleEn: 'Take an order',
  titleAr: 'استلام طلب',
  blurbEn:
    'Takes what they want and how it reaches them, and skips the address when they are collecting.',
  blurbAr: 'يأخذ الطلب وطريقة الاستلام، ويتخطى العنوان إذا كان بيستلم بنفسه.',
  taskTitleEn: 'Take an order',
  taskTitleAr: 'استلام طلب',
  triggerEn:
    'The customer wants to place an order or asks how to buy something.',
  triggerAr: 'العميل يبي يطلب شي أو يسأل كيف يشتري.',
  definition: {
    firstStepId: 'what',
    steps: [
      {
        id: 'what',
        name: 'What they want',
        type: 'ask',
        kind: 'text',
        instruction:
          'Ask what the customer would like to order, and how many of each. Do not quote a price.',
        questionEn: 'What would you like to order?',
        questionAr: 'شنو تحب تطلب؟',
        saveTo: { chat: 'order' },
        next: 'how',
      },
      {
        id: 'how',
        name: 'Delivery or collection',
        type: 'choose',
        instruction:
          'Ask whether they want it delivered or will collect it. Do not list the choices as text. They are shown as buttons.',
        questionEn: 'Delivery or pickup?',
        questionAr: 'توصيل أو استلام؟',
        options: [
          { id: 'delivery', labelEn: 'Deliver it', labelAr: 'توصيل' },
          {
            id: 'pickup',
            labelEn: 'I will collect it',
            labelAr: 'أستلمه بنفسي',
            // The only jump in any starter, and it is the reason this starter exists:
            // an address is the wrong question for somebody who is coming to you.
            next: 'who',
          },
        ],
        saveTo: { chat: 'fulfilment' },
        next: 'area',
      },
      {
        id: 'area',
        name: 'Where to deliver',
        type: 'ask',
        kind: 'text',
        instruction:
          'Ask which area in Kuwait the order goes to, with the block and the street if they have them.',
        questionEn: 'Which area should we deliver to?',
        questionAr: 'لأي منطقة نوصل الطلب؟',
        saveTo: { chat: 'area' },
        next: 'who',
      },
      {
        id: 'who',
        name: 'Their name',
        type: 'ask',
        kind: 'text',
        instruction: 'Ask for the name the order should be under.',
        questionEn: 'What name should we put on the order?',
        questionAr: 'باسم منو نسجل الطلب؟',
        saveTo: { chat: 'caller_name', contact: 'name' },
        next: 'number',
      },
      {
        id: 'number',
        name: 'Their number',
        type: 'ask',
        kind: 'phone',
        instruction:
          'Ask which number to call about the order. Accept the number they are writing from.',
        questionEn: 'Which number should we call about it?',
        questionAr: 'على أي رقم نتواصل وياك؟',
        saveTo: { chat: 'caller_phone', contact: 'phone_number' },
        next: 'read_back',
      },
      {
        id: 'read_back',
        name: 'Read it back',
        type: 'tell',
        instruction:
          'Read the order back to the customer, with where it is going or that they are collecting it, and say a colleague will confirm the total shortly. Do not quote a price and do not promise a delivery day.',
        next: 'pass_it_on',
      },
      {
        id: 'pass_it_on',
        name: 'A person confirms it',
        type: 'handoff',
        note: 'An order is ready. What they want, how it reaches them, the name and the number are in the answers above. Confirm the total and the day.',
      },
    ],
  },
  script: [
    typed('I want to order', 'أبي أطلب'),
    say('What would you like to order?', 'شنو تحب تطلب؟'),
    typed('Two boxes of the dark roast', 'صندوقين من المحمص الغامق'),
    say('Delivery or pickup?', 'توصيل أو استلام؟', {
      options: [
        option('delivery', 'Deliver it', 'توصيل'),
        option('pickup', 'I will collect it', 'أستلمه بنفسي'),
      ],
    }),
    typed('Deliver it', 'توصيل'),
    say('Which area should we deliver to?', 'لأي منطقة نوصل الطلب؟', {
      noteEn: 'Asked only when they chose delivery.',
      noteAr: 'يُسأل فقط إذا اختار التوصيل.',
    }),
    typed('Jabriya, block 4', 'الجابرية، قطعة ٤'),
    say('What name should we put on the order?', 'باسم منو نسجل الطلب؟'),
    typed('Dana', 'دانة'),
    say('Which number should we call about it?', 'على أي رقم نتواصل وياك؟'),
    typed('99887766', '٩٩٨٨٧٧٦٦'),
    say(
      'Two boxes of the dark roast to Jabriya, under Dana. A colleague will confirm the total shortly.',
      'صندوقين محمص غامق للجابرية، باسم دانة. أحد الزملاء بيأكد لك المبلغ بعد شوي.',
      {
        noteEn: 'Then the chat comes to you, with the order written down.',
        noteAr: 'وبعدها تنتقل المحادثة لك، والطلب مكتوب معها.',
      }
    ),
  ],
};

// ── 3 · take an enquiry ───────────────────────────────────────────────────────────────

const ENQUIRY = {
  id: 'take_an_enquiry',
  icon: 'message-square-quote',
  titleEn: 'Take an enquiry',
  titleAr: 'استلام استفسار',
  blurbEn:
    'Finds out what they need and how to reach them, without guessing a price.',
  blurbAr: 'يعرف شنو يحتاج العميل وكيف نتواصل وياه، بدون ما يخمّن سعر.',
  taskTitleEn: 'Take an enquiry',
  taskTitleAr: 'استلام استفسار',
  triggerEn:
    'The customer asks about prices or services, or wants somebody to get back to them.',
  triggerAr: 'العميل يسأل عن الأسعار أو الخدمات، أو يبي أحد يرد عليه.',
  definition: {
    firstStepId: 'about',
    steps: [
      {
        id: 'about',
        name: 'What it is about',
        type: 'choose',
        instruction:
          'Ask what the enquiry is about. Do not list the choices as text. They are shown as buttons.',
        questionEn: 'What is your enquiry about?',
        questionAr: 'استفسارك عن شنو؟',
        options: [
          { id: 'prices', labelEn: 'Prices', labelAr: 'الأسعار' },
          { id: 'services', labelEn: 'What you offer', labelAr: 'الخدمات' },
          { id: 'other', labelEn: 'Something else', labelAr: 'شي ثاني' },
        ],
        saveTo: { chat: 'enquiry_about' },
        next: 'detail',
      },
      {
        id: 'detail',
        name: 'What they need',
        type: 'ask',
        kind: 'text',
        instruction:
          'Ask the customer to describe what they need in their own words. Do not answer it yourself and do not quote a price.',
        questionEn: 'Tell us a little more. What exactly do you need?',
        questionAr: 'عطنا تفاصيل أكثر. شنو تحتاج بالضبط؟',
        saveTo: { chat: 'enquiry_detail' },
        next: 'who',
      },
      {
        id: 'who',
        name: 'Their name',
        type: 'ask',
        kind: 'text',
        instruction: 'Ask for the name we should use when we get back to them.',
        questionEn: 'And what name should we ask for?',
        questionAr: 'وعلى أي اسم نسأل؟',
        saveTo: { chat: 'caller_name', contact: 'name' },
        next: 'number',
      },
      {
        id: 'number',
        name: 'Their number',
        type: 'ask',
        kind: 'phone',
        instruction:
          'Ask which number to reach them on. Accept the number they are writing from.',
        questionEn: 'Which number should we reach you on?',
        questionAr: 'على أي رقم نقدر نتواصل وياك؟',
        saveTo: { chat: 'caller_phone', contact: 'phone_number' },
        next: 'read_back',
      },
      {
        id: 'read_back',
        name: 'Read it back',
        type: 'tell',
        instruction:
          'Repeat back what they asked about and say a colleague will get back to them shortly. Do not quote a price and do not promise a time.',
        next: 'pass_it_on',
      },
      {
        id: 'pass_it_on',
        name: 'A person answers it',
        type: 'handoff',
        note: 'An enquiry is ready. What it is about, what they need, the name and the number are in the answers above. Get back to them.',
      },
    ],
  },
  script: [
    typed('How much do you charge?', 'كم أسعاركم؟'),
    say('What is your enquiry about?', 'استفسارك عن شنو؟', {
      options: [
        option('prices', 'Prices', 'الأسعار'),
        option('services', 'What you offer', 'الخدمات'),
        option('other', 'Something else', 'شي ثاني'),
      ],
      noteEn: 'Write your own choices here, up to ten.',
      noteAr: 'اكتب خياراتك أنت هنا، حتى عشرة.',
    }),
    typed('Prices', 'الأسعار'),
    say(
      'Tell us a little more. What exactly do you need?',
      'عطنا تفاصيل أكثر. شنو تحتاج بالضبط؟'
    ),
    typed('A deep clean for a two-bedroom flat', 'تنظيف شامل لشقة غرفتين'),
    say('And what name should we ask for?', 'وعلى أي اسم نسأل؟'),
    typed('Fatma', 'فاطمة'),
    say('Which number should we reach you on?', 'على أي رقم نقدر نتواصل وياك؟'),
    typed('55443322', '٥٥٤٤٣٣٢٢'),
    say(
      'A deep clean for a two-bedroom flat, under Fatma. A colleague will get back to you shortly.',
      'تنظيف شامل لشقة غرفتين، باسم فاطمة. أحد الزملاء بيتواصل وياك بعد شوي.',
      {
        noteEn: 'No price is ever guessed. A person answers that.',
        noteAr: 'ما يخمّن أي سعر أبداً. الرد على هذا من شخص.',
      }
    ),
  ],
};

/** In the order the cards are drawn. */
export const STARTERS = Object.freeze([BOOK, ORDER, ENQUIRY]);

export const STARTER_IDS = Object.freeze(STARTERS.map(s => s.id));

const BY_ID = new Map(STARTERS.map(s => [s.id, s]));

/** One starter, or `null` for an id this list has never had. Total; never throws. */
export const starterById = id => BY_ID.get(id) ?? null;

/**
 * A deep copy, because a starter becomes the owner's draft the moment she picks it and the
 * table above must not become editable through it. `structuredClone` is not used: the
 * harness and jsdom both have it, but a frozen literal of plain JSON is exactly what
 * `JSON.parse(JSON.stringify(…))` is total on, and it also drops anything unserialisable
 * rather than carrying it into a draft that is about to be sent.
 */
export const starterDefinition = id => {
  const found = starterById(id);
  return found ? JSON.parse(JSON.stringify(found.definition)) : null;
};

export default STARTERS;
